/**
 * Sphere coordinate generation
 * Converts lat/lon grid to 3D coordinates
 *
 * Ported from Python (map_projection/earth_model.py lines 109-169)
 */

import { PROJECTIONS_INVERSE } from './projections.js';

/**
 * Generate 3D sphere coordinates for each pixel corner
 * This function creates a sphere representation of the image by converting
 * each pixel's corners from 2D projection coordinates to 3D sphere coordinates.
 *
 * @param {number} width - Image width in pixels
 * @param {number} height - Image height in pixels
 * @param {string} projection - Projection name (e.g., 'equirectangular', 'mercator')
 * @returns {Float32Array} Sphere coordinates for all pixel corners
 *                         Format: [x_tl, y_tl, z_tl, x_tr, y_tr, z_tr, x_br, y_br, z_br, x_bl, y_bl, z_bl, ...]
 *                         Shape: (height * width * 4 corners * 3 coords)
 */
export function generateSphere(width, height, projection) {
    const inverseProjection = PROJECTIONS_INVERSE[projection];
    if (!inverseProjection) {
        throw new Error(`No inverse projection found for: ${projection}`);
    }

    const numPixels = width * height;
    // 4 corners per pixel, 3 coordinates (x,y,z) per corner
    const sphereCoords = new Float32Array(numPixels * 4 * 3);

    let writeIndex = 0;

    // For each pixel in the image
    for (let row = 0; row < height; row++) {
        for (let col = 0; col < width; col++) {
            // Define the four corners of the pixel:
            // Top-Left (TL), Top-Right (TR), Bottom-Right (BR), Bottom-Left (BL)
            const corners = [
                [col, row],         // TL
                [col + 1, row],     // TR
                [col + 1, row + 1], // BR
                [col, row + 1],     // BL
            ];

            // Convert each corner to sphere coordinates
            for (const [x, y] of corners) {
                // Normalize pixel coordinates to [0, 1]
                const xNorm = x / width;
                const yNorm = y / height;

                // Apply inverse projection to get lat/lon in radians
                const [lat, lon] = inverseProjection(xNorm, yNorm);

                // Convert spherical coordinates (lat, lon) to 3D Cartesian coordinates
                // Using sphere of radius 1:
                // x = cos(lat) * cos(lon)
                // y = cos(lat) * sin(lon)
                // z = sin(lat)
                const sphereX = Math.cos(lat) * Math.cos(lon);
                const sphereY = Math.cos(lat) * Math.sin(lon);
                const sphereZ = Math.sin(lat);

                sphereCoords[writeIndex++] = sphereX;
                sphereCoords[writeIndex++] = sphereY;
                sphereCoords[writeIndex++] = sphereZ;
            }
        }
    }

    return sphereCoords;
}
